import {backendWeaveExecutionUrl, backendWeaveHost} from '@wandb/common/config';
import {
  BasicClient,
  bindClientToGlobalThis,
  CachedClient,
  Client,
  createLocalServer,
  createRemoteServer,
  createServerWithShadow,
  LocalServer,
  LOG_DEBUG_MESSAGES,
  OpStore,
  Server,
} from '@wandb/weave-ui/cg';
import {GlobalCGReactTracker} from '@wandb/weave-ui/cgreact';
import {useLoadWeaveObjects} from '@wandb/weave-ui/components/Panel2/weaveBackend';
import {ComputeGraphContextProviderFromClient} from '@wandb/weave-ui/contextProviders';
import {mapKeys, omit} from 'lodash';
import React, {useMemo} from 'react';

import * as ServerApiProd from './cgservers/serverApiProd';
import {Variant, VariantWrapper} from './components/ExperimentVariant';
import {auth} from './setup';
import {useApolloClient} from './state/hooks';
import {useViewer} from './state/viewer/hooks';
import {viewerUsingAdminPrivileges} from './util/admin';
import {useBetaFeatureWeave1} from './util/useBetaFeature';

const useClient = (server: Server): Client => {
  return useMemo(() => {
    let client: Client = new BasicClient(server);
    if (!(server instanceof LocalServer)) {
      client = new CachedClient(client);
    }
    bindClientToGlobalThis(client);
    return client;
  }, [server]);
};

const useLocalServer = () => {
  const apolloClient = useApolloClient();
  return useMemo(() => {
    const serverApi = new ServerApiProd.Client(apolloClient);
    return createLocalServer(serverApi);
  }, [apolloClient]);
};

const useRemoteServer = (skip: boolean, opStore?: OpStore) => {
  const {loading, remoteOpStore} = useLoadWeaveObjects(skip || !!opStore);
  return useMemo(() => {
    const finalStore: OpStore | null = opStore || remoteOpStore;

    if (!finalStore || (!opStore && loading)) {
      return null;
    }

    return createRemoteServer(
      backendWeaveExecutionUrl(),
      auth.ensureCurrentIdToken.bind(auth),
      finalStore,
      viewerUsingAdminPrivileges(),
      false,
      // we force the use of window.fetch here because we
      // need to use the version of fetch that datadog and
      // other tracing tools have instrumented
      (input, init) => window.fetch(input, init)
    );
  }, [loading, remoteOpStore, opStore]);
};

const useRemoteShadowServer = (skip: boolean, opStore: OpStore) => {
  const remoteServer = useMemo(() => {
    return createRemoteServer(
      backendWeaveExecutionUrl(true),
      auth.ensureCurrentIdToken.bind(auth),
      opStore,
      viewerUsingAdminPrivileges(),
      true,
      // we force the use of window.fetch here because we
      // need to use the version of fetch that datadog and
      // other tracing tools have instrumented
      (input, init) => window.fetch(input, init)
    );
  }, [opStore]);
  return skip ? null : remoteServer;
};

const useServerWithShadow = (
  mainServer: Server,
  shadowServer: Server | null
) => {
  return useMemo(() => {
    if (!shadowServer) {
      return null;
    }
    return createServerWithShadow(mainServer, shadowServer);
  }, [mainServer, shadowServer]);
};

enum WeaveVersion {
  V0 = 0,
  V1 = 1,
}

const ComputeGraphContextProviderInner: React.FC<{
  shadowEnabled: boolean;
  prodTrafficWeaveVersion: WeaveVersion;
}> = ({children, shadowEnabled, prodTrafficWeaveVersion}) => {
  const hasRemote = !!backendWeaveHost();
  const featureFlagSetting = useBetaFeatureWeave1();

  const weave1Enabled =
    useBetaFeatureWeave1() ||
    // if the feature flag is not set, we want to route 100% of traffic to weave1 if the user
    // is in the experiment otherwise, we want the feature flag to control the routing
    (prodTrafficWeaveVersion === WeaveVersion.V1 && featureFlagSetting == null);

  const localServer = useLocalServer();
  const remoteShadowServer = useRemoteShadowServer(
    !(hasRemote && shadowEnabled),
    localServer.opStore
  );

  const remoteExecutionServer = useRemoteServer(
    !(hasRemote && weave1Enabled),
    localServer.opStore
  );

  const localServerWithShadow = useServerWithShadow(
    localServer,
    remoteShadowServer
  );

  let server: Server;
  if (weave1Enabled && remoteExecutionServer) {
    server = remoteExecutionServer;
  } else if (shadowEnabled && localServerWithShadow) {
    server = localServerWithShadow;
  } else {
    server = localServer;
  }

  const client = useClient(server);
  return (
    <ComputeGraphContextProviderFromClient
      client={client}
      children={children}
    />
  );
};

export const ComputeGraphContextProvider: React.FC = React.memo(
  ({children}) => {
    const viewer = useViewer();
    const weave1ExperimentId =
      'RXhwZXJpbWVudDo5OTkwYTU3Zi00NjFjLTQ4NTgtOGY1Zi00NTE0Y2E1NmQ0ZDg=';
    const shadowExperimentId =
      'RXhwZXJpbWVudDpkZjNjZDViZS01YTY5LTQ4YjMtYTZlMi0wMTk1ZTg1ODlhYzE=';
    return (
      <VariantWrapper
        graphqlExperimentId={weave1ExperimentId}
        observationalUnitId={viewer?.id ?? ''}>
        <Variant bucket={0} control>
          <VariantWrapper
            // feature flag for weave shadow service routing. requests for N% (configurable) of users to
            // local engine are routed to shadow service
            graphqlExperimentId={shadowExperimentId}
            observationalUnitId={viewer?.id ?? ''}>
            <Variant bucket={0} control>
              <ComputeGraphContextProviderInner
                children={children}
                shadowEnabled={false}
                prodTrafficWeaveVersion={WeaveVersion.V0}
              />
            </Variant>
            <Variant bucket={1}>
              <ComputeGraphContextProviderInner
                children={children}
                shadowEnabled={true}
                prodTrafficWeaveVersion={WeaveVersion.V0}
              />
            </Variant>
          </VariantWrapper>
        </Variant>
        <Variant bucket={1}>
          <ComputeGraphContextProviderInner
            children={children}
            shadowEnabled={false}
            prodTrafficWeaveVersion={WeaveVersion.V1}
          />
        </Variant>
      </VariantWrapper>
    );
  }
);

//// Logging Execution Metrics ////

const globalCGReactTrackerInterval = 30000;
let hasStarted = false;

const sumDict = (dict: {[key: string]: any}): number => {
  return Object.values(dict).reduce(
    (acc, val) => acc + (typeof val === 'number' ? val : sumDict(val)),
    0
  );
};

const flattenDict = (dict: {[key: string]: any}): {[key: string]: number} => {
  let flatDict: {[key: string]: number} = {};
  for (const key in dict) {
    if (typeof dict[key] === 'number') {
      flatDict[key] = dict[key];
    } else {
      flatDict = {
        ...flatDict,
        ...mapKeys(flattenDict(dict[key]), (v, k) => `${key}.${k}`),
      };
    }
  }
  return flatDict;
};

const logGlobalCGReactTracker = () => {
  hasStarted = true;
  const summary = GlobalCGReactTracker.summary();
  // Ignore duration (since it is always non-negative) and _0_useNodeValue since
  // this has super high volume, but high cache rates. This will reduce the number
  // of log events produced, but not the accuracy of the metrics.
  const sumOfValues = sumDict(omit(summary, ['__duration', '_0_useNodeValue']));
  if (sumOfValues > 0) {
    if (LOG_DEBUG_MESSAGES) {
      console.log('cg-react-tracker', flattenDict(summary));
    }
    window.analytics?.track('cg-react-tracker', summary);
    GlobalCGReactTracker.reset();
  }
  setTimeout(logGlobalCGReactTracker, globalCGReactTrackerInterval);
};

if (!hasStarted) {
  setTimeout(logGlobalCGReactTracker, globalCGReactTrackerInterval);
}
